import React from "react";
import { useNavigate } from "react-router-dom";
import { IconBrandLinkedin, IconBrandInstagram } from "@tabler/icons-react";
import { cn } from "@lemonadetraining/web-ui";
import logoImage from "../assets/lemon-logo.png";
// Read from the manifest this app is built from: a separately stored copy of the
// version drifts silently the moment a release bumps one and not the other, and a
// stale version number raises no error — it just misreports the build to visitors.
import packageJson from "../../package.json";

interface FooterLink {
  label: string;
  path: string;
  external?: boolean;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const footerSections: FooterSection[] = [
  {
    title: "Product",
    links: [
      { label: "Lemonade Business", path: "/lemonade-business" },
      { label: "Lemonade Coach", path: "/lemonade-coach" },
      { label: "For Athletes", path: "/for-athletes" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", path: "/About-Us" },
      { label: "info@lemonade.training", path: "mailto:info@lemonade.training", external: true },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", path: "/privacy-policy" },
      { label: "Cookies", path: "/privacy-policy#cookies" },
      { label: "Terms & Conditions", path: "/terms-and-conditions" },
      { label: "Accessibility Statement", path: "/accessibility" },
    ],
  },
];

const socialLinks = [
  {
    icon: <IconBrandLinkedin size={20} />,
    label: "LinkedIn",
    url: "https://linkedin.com/company/lemonade-training",
  },
  {
    // Tabler draws Instagram in 16 of 24 units vs LinkedIn's 18: scale it up, keep the stroke width equal
    icon: <IconBrandInstagram size={22.5} strokeWidth={(2 * 20) / 22.5} />,
    label: "Instagram",
    url: "https://www.instagram.com/lemonade.training",
  },
];

const LINK =
  "inline-flex min-h-11 items-center lg:min-h-0 w-fit text-sm text-white/60 transition-colors duration-200 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary focus-visible:rounded";

interface FooterProps {
  className?: string;
}

function Footer({ className }: FooterProps) {
  const navigate = useNavigate();

  const handleNavigation = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer
      role="contentinfo"
      aria-label="Site footer"
      className={cn("relative z-10 mt-auto bg-black text-white", className)}
    >
      <div
        aria-hidden="true"
        className="h-px bg-[linear-gradient(to_right,transparent,rgba(255,255,255,0.18)_42%,rgba(255,255,255,0.18)_58%,transparent)]"
      />
      <div className="mx-auto max-w-[1400px] w-[calc(100%-2rem)] md:w-[calc(100%-240px)] py-10 md:py-12">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <a
              href="/"
              onClick={handleNavigation("/")}
              className="mb-4 inline-flex min-h-11 items-center transition-opacity duration-200 hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary focus-visible:rounded-lg"
              aria-label="Lemonade Training - Go to homepage"
            >
              <img
                src={logoImage}
                alt="Lemonade Training Logo"
                className="h-8 w-auto object-contain"
              />
            </a>
            <p className="text-xs leading-relaxed text-white/55 max-w-[260px]">
              Lemonade Training™ is a registered trademark in the European Union.
            </p>
          </div>

          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="mb-4 text-xs leading-none uppercase tracking-[0.12em] text-white/55">
                {section.title}
              </h3>
              <nav aria-label={`${section.title} navigation`} className="flex flex-col lg:gap-2.5">
                {section.links.map((link) =>
                  link.external ? (
                    <a key={link.path} href={link.path} className={LINK}>
                      {link.label}
                    </a>
                  ) : (
                    <a
                      key={link.path}
                      href={link.path}
                      onClick={handleNavigation(link.path)}
                      className={LINK}
                    >
                      {link.label}
                    </a>
                  )
                )}
                {section.title === "Company" && (
                  <div role="group" className="-ml-3 flex" aria-label="Social media links">
                    {socialLinks.map((social) => (
                      <a
                        key={social.label}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Visit our ${social.label}`}
                        className="flex h-11 w-11 items-center justify-center rounded-lg text-white/60 transition-colors duration-200 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                      >
                        {social.icon}
                      </a>
                    ))}
                  </div>
                )}
              </nav>
            </div>
          ))}
        </div>

        <div
          aria-hidden="true"
          className="my-8 h-px bg-[linear-gradient(to_right,transparent,rgba(255,255,255,0.18)_42%,rgba(255,255,255,0.18)_58%,transparent)]"
        />

        <div className="flex flex-col gap-2 text-xs text-white/55 md:flex-row md:items-center md:justify-between">
          <p>© {currentYear} Lemonade Training Oy. All rights reserved.</p>
          <p>Made with ❤️ in Finland • v{packageJson.version}</p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
